import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Card, Collapse, Divider, Skeleton, Stack, Typography } from '@mui/material';

import { getProjectCohortPath } from '@verantos-dev/core-constants';
import { borderRadius, fontWeight } from '@verantos-dev/shared-ui-components/tokens';

import { AddCohortPickerModal } from './AddCohortPickerModal';
import { SourceCohortRow } from '../../components/SourceCohortRow';
import { useSourceCohortsCard } from '../../hooks/useSourceCohortsCard';
import type { SourceCohortsCardProps } from '../../types';

// The analysis's source cohorts as an expandable card: "Cohorts" and how many in the
// header with the study period they share, and the cohorts listed beneath. It lists
// whatever cohorts it is given, so it holds for the several an analysis will have;
// until then it shows the one it has. Open by default, and remembered once toggled.
export const SourceCohortsCard = ({ isLoading = false }: SourceCohortsCardProps) => {
  const {
    projectId,
    title,
    cohorts,
    isLoading: showSkeleton,
    isExpanded,
    onToggle,
    toggleLabel,
    contentId,
    studyPeriodLabel,
    studyPeriodText,
    isStudyPeriodSet,
    notSelectedText,
    staleCohortTooltip,
    switchLabel,
    onSwitchCohort,
    isEditDisabled,
    editLockTooltip,
    isPickerOpen,
    onClosePicker,
  } = useSourceCohortsCard(isLoading);

  if (showSkeleton) {
    return (
      <Skeleton
        variant="rounded"
        height={96}
        animation="wave"
        sx={{ borderRadius: `${borderRadius.md}px`, flexShrink: 0 }}
        data-testid="analysis-source-cohorts-loading"
      />
    );
  }

  return (
    <>
      {/* flexShrink 0: a card clips its content, so in the tab's column it would
          otherwise be squeezed to its border when the tables below need the height. */}
      <Card
        variant="outlined"
        data-testid="analysis-source-cohorts-card"
        sx={{ minWidth: 0, flexShrink: 0 }}
      >
        {/* Baseline-aligned, so the title, the count, the toggle and the study period
            sit on one line despite their different sizes, with an even gap between. */}
        <Stack
          direction="row"
          alignItems="baseline"
          justifyContent="space-between"
          flexWrap="wrap"
          columnGap={4}
          rowGap={1}
          sx={{ px: 4, py: 1.5 }}
          data-testid="analysis-source-cohorts-header"
        >
          <Stack direction="row" alignItems="baseline" spacing={2}>
            <Typography variant="body1" sx={{ fontWeight: fontWeight.semibold }}>
              {title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="analysis-source-cohorts-count"
            >
              {cohorts.length}
            </Typography>
            <Button
              size="small"
              onClick={onToggle}
              aria-expanded={isExpanded}
              aria-controls={contentId}
              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              // No padding of its own, so the gap before it matches the others.
              sx={{ textTransform: 'none', minWidth: 0, p: 0, typography: 'body2' }}
              data-testid="analysis-source-cohorts-toggle"
            >
              {toggleLabel}
            </Button>
          </Stack>
          <Stack direction="row" alignItems="baseline" spacing={2}>
            <Typography variant="body2" sx={{ fontWeight: fontWeight.semibold }}>
              {studyPeriodLabel}
            </Typography>
            <Typography
              variant="body2"
              color={isStudyPeriodSet ? 'text.primary' : 'text.secondary'}
              data-testid="analysis-source-cohorts-study-period"
            >
              {studyPeriodText}
            </Typography>
          </Stack>
        </Stack>

        <Collapse in={isExpanded} unmountOnExit id={contentId}>
          <Divider sx={{ borderColor: 'border.subtle' }} />
          {cohorts.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 4, py: 2 }}
              data-testid="analysis-source-cohorts-empty"
            >
              {notSelectedText}
            </Typography>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {cohorts.map((cohort, index) => (
                <Box component="li" key={cohort.cohortProfileId}>
                  {index > 0 && <Divider sx={{ borderColor: 'border.subtle' }} />}
                  <SourceCohortRow
                    cohort={cohort}
                    href={getProjectCohortPath(projectId, cohort.cohortProfileId)}
                    staleTooltip={staleCohortTooltip}
                    switchLabel={switchLabel}
                    onSwitch={onSwitchCohort}
                    isSwitchDisabled={isEditDisabled}
                    switchDisabledTooltip={editLockTooltip}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Collapse>
      </Card>
      {isPickerOpen && <AddCohortPickerModal projectId={projectId} onClose={onClosePicker} />}
    </>
  );
};
